import { Status as GoogleMapsWrapperStatus } from "@googlemaps/react-wrapper"
import {
  Address,
  Address_Country as Country,
} from "gen/proto/address/models_pb"
import { googleMapsCountryCode } from "shared-ts/libs/country"
import usePlacesAutocomplete, {
  getGeocode,
  getZipCode,
} from "use-places-autocomplete"

export interface AutocompleteAddress extends Address {
  placeId?: string
  description?: string
}

export function useAddressAutocomplete(country: Country) {
  const {
    suggestions,
    setValue,
    init: initUsePlacesAutocomplete,
  } = usePlacesAutocomplete({
    requestOptions: {
      componentRestrictions: { country: googleMapsCountryCode(country) },
      types: ["address"],
    },
    initOnMount: false,
  })

  const addresses: AutocompleteAddress[] = suggestions.data
    .filter((s) => s.description.split(",").length >= 3)
    .map((s) => {
      const [line1, city, state] = s.description.split(",")

      return {
        line1,
        line2: "",
        city,
        state,
        postalCode: "",
        placeId: s.place_id,
        description: s.description,
        country,
      }
    })

  async function resolveAddress(placeId: string): Promise<Address> {
    const geocodeResults = await getGeocode({ placeId })
    const postalCode = getZipCode(geocodeResults[0], false)

    let line1 = ""
    let locality = ""
    let sublocality = ""
    let state = ""
    for (const addressComponent of geocodeResults[0].address_components) {
      const {
        short_name: shortName,
        long_name: longName,
        types,
      } = addressComponent
      if (types.includes("street_number")) {
        line1 = `${longName} ${line1}`.trim()
      } else if (types.includes("route")) {
        line1 = `${line1} ${shortName}`.trim()
      } else if (types.includes("locality")) {
        locality = longName
      } else if (
        types.includes("sublocality_level_1") ||
        types.includes("sublocality")
      ) {
        sublocality = longName
      } else if (types.includes("administrative_area_level_1")) {
        state = shortName
      }
    }

    return {
      line1,
      line2: "",
      city: locality || sublocality,
      state,
      postalCode: postalCode ?? "",
      country,
    }
  }

  function googleMapsWrapperCallback(status: GoogleMapsWrapperStatus) {
    if (status === GoogleMapsWrapperStatus.SUCCESS) {
      initUsePlacesAutocomplete()
    }
  }

  return { addresses, setValue, resolveAddress, googleMapsWrapperCallback }
}
